import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { ProjectHome } from '../features/beat/ProjectHome';
import { ProjectsScreen } from '../features/beat/ProjectsScreen';
import { DigScreen } from '../features/dig/DigScreen';
import { DrumsLayout } from '../features/drums/DrumsLayout';
import { MoreScreen, MoreSection } from '../features/more/MoreScreen';
import { CrashScreen } from '../features/CrashScreen';
import { NotFound } from '../features/NotFound';
import { Progress } from '../ui/States';
import { Layout } from './Layout';
import { useAppStore } from './store';

// Lazy: Learn content (CLAUDE.md performance) and the dev gallery.
const LearnScreen = lazy(() => import('../features/learn/LearnScreen'));
const StyleSheetScreen = lazy(() => import('../features/learn/StyleSheetScreen'));
const LowEndScreen = lazy(() => import('../features/learn/LowEndScreen'));
const MpcToolboxScreen = lazy(() => import('../features/learn/MpcToolboxScreen'));
const CrateGuideScreen = lazy(() => import('../features/learn/CrateGuideScreen'));
const SpectrumScreen = lazy(() => import('../features/learn/SpectrumScreen'));
const EqScreen = lazy(() => import('../features/learn/EqScreen'));
const CompressionScreen = lazy(() => import('../features/learn/CompressionScreen'));
const VocalsScreen = lazy(() => import('../features/learn/VocalsScreen'));
const TempoFxScreen = lazy(() => import('../features/learn/TempoFxScreen'));
const MasterScreen = lazy(() => import('../features/learn/MasterScreen'));
const FixesScreen = lazy(() => import('../features/learn/FixesScreen'));
const EqTrainerScreen = lazy(() => import('../features/learn/EqTrainerScreen'));
const EqTrainScreen = lazy(() => import('../features/learn/EqTrainScreen'));
const CompTrainScreen = lazy(() => import('../features/learn/CompTrainScreen'));
const CompTrainerScreen = lazy(() => import('../features/learn/CompTrainerScreen'));
const ChordMatchScreen = lazy(() => import('../features/harmony/ChordMatchScreen'));
const LibraryScreen = lazy(() => import('../features/drums/library/LibraryScreen'));
const KitScreen = lazy(() => import('../features/drums/kit/KitScreen'));
const GrooveScreen = lazy(() => import('../features/drums/groove/GrooveScreen'));
const StorageScreen = lazy(() => import('../features/more/StorageScreen'));
const BackupScreen = lazy(() => import('../features/more/BackupScreen'));
const AboutScreen = lazy(() => import('../features/more/AboutScreen'));
const VerifyScreen = lazy(() => import('../features/more/VerifyScreen'));
const MarksScreen = lazy(() => import('../features/dig/MarksScreen'));
const HistoryScreen = lazy(() => import('../features/dig/HistoryScreen'));
const GuideScreen = lazy(() => import('../features/dig/GuideScreen'));
const ChannelsScreen = lazy(() => import('../features/dig/ChannelsScreen'));
const ComponentGallery = lazy(() => import('../features/dev/ComponentGallery'));
// The audio engine's Playwright harness (v0.5). Only the dev server or a VITE_DEV_HARNESS build has it: in a
// production build both flags fold to false, so this import and its chunk are removed (scripts/check-no-harness.ts).
const AudioHarness = import.meta.env.DEV || import.meta.env.VITE_DEV_HARNESS ? lazy(() => import('../features/dev/AudioHarness')) : undefined;

// Precached, so this is normally instant; after 300 ms it says what's loading (fail list 12).
const later = (node: ReactNode) => (
  <Suspense fallback={<div style={{ padding: 'var(--s4)' }}><Progress active label="Loading" /></div>}>{node}</Suspense>
);

/** Relaunch opens where you left off (DESIGN_SYSTEM §5). */
function Resume() {
  const lastPath = useAppStore((s) => s.continuity.lastPath);
  return <Navigate to={lastPath || '/dig'} replace />;
}

function DrumsIndex() {
  const last = useAppStore((s) => s.continuity.tabPaths.drums);
  // The Drums tab reopens where you were, but never inside a project's kit or groove: that comes from the project
  const path = last?.replace(/([?&])project=[^&]*&?/, '$1').replace(/[?&]$/, '');
  return <Navigate to={path && path !== '/drums' ? path : '/drums/library'} replace />;
}

// Every screen has a URL (SPEC §4).
export const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [{
    errorElement: <CrashScreen />,
    children: [
      { index: true, element: <Resume /> },
      { path: 'dig', element: <DigScreen /> },
      { path: 'dig/marks', element: later(<MarksScreen />) },
      { path: 'dig/history', element: later(<HistoryScreen />) },
      { path: 'dig/channels', element: later(<ChannelsScreen />) },
      { path: 'dig/guide', element: later(<GuideScreen />) },
      {
        path: 'drums',
        element: <DrumsLayout />,
        children: [
          { index: true, element: <DrumsIndex /> },
          { path: 'library', element: later(<LibraryScreen />) },
          { path: 'kit', element: later(<KitScreen />) },
          { path: 'groove', element: later(<GrooveScreen />) },
        ],
      },
      { path: 'beat', element: <ProjectsScreen /> },
      { path: 'beat/:projectId', element: <ProjectHome /> },
      { path: 'learn', element: later(<LearnScreen />) },
      { path: 'learn/style', element: later(<StyleSheetScreen />) },
      { path: 'learn/low-end', element: later(<LowEndScreen />) },
      { path: 'learn/mpc-toolbox', element: later(<MpcToolboxScreen />) },
      { path: 'learn/crate-guide', element: later(<CrateGuideScreen />) },
      { path: 'learn/spectrum', element: later(<SpectrumScreen />) },
      { path: 'learn/eq', element: later(<EqScreen />) },
      { path: 'learn/compression', element: later(<CompressionScreen />) },
      { path: 'learn/vocals', element: later(<VocalsScreen />) },
      { path: 'learn/tempo-fx', element: later(<TempoFxScreen />) },
      { path: 'learn/master', element: later(<MasterScreen />) },
      { path: 'learn/fixes', element: later(<FixesScreen />) },
      { path: 'learn/eq-trainer', element: later(<EqTrainerScreen />) },
      { path: 'learn/eq-trainer/train', element: later(<EqTrainScreen />) },
      { path: 'learn/comp-trainer', element: later(<CompTrainerScreen />) },
      { path: 'learn/comp-trainer/train', element: later(<CompTrainScreen />) },
      { path: 'learn/chord-match', element: later(<ChordMatchScreen />) },
      { path: 'more', element: <MoreScreen /> },
      { path: 'more/storage', element: later(<StorageScreen />) },
      { path: 'more/backup', element: later(<BackupScreen />) },
      { path: 'more/about', element: later(<AboutScreen />) },
      { path: 'more/verify', element: later(<VerifyScreen />) },
      { path: 'more/:section', element: <MoreSection /> },
      { path: 'dev/components', element: later(<ComponentGallery />) },
      ...(AudioHarness ? [{ path: 'dev/audio', element: later(<AudioHarness />) }] : []),
      { path: '*', element: <NotFound /> },
    ] }],
  },
];
